import { CloseOutlined } from '@ant-design/icons';
import { Tooltip } from '@mm/components';
import {
  myLeadsExcludeTypes,
  MyLeadsFilterTypes,
  myLeadsIncludeTypes,
  MyLeadsSearchQuery,
  MyLeadsUrlsQueryType,
} from '@mm/state/interfaces/myLeads';
import classNames from 'classnames';
import { isEqual, uniqueId } from 'lodash';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import PrimarySelect, { DropdownOption } from '../PrimarySelect/PrimarySelect';
import { RemoteSearchInput } from '../RemoteSearchInput/RemoteSearchInput';
import styles from './LandingPagesRemoteSearch.module.css';

type Props = {
  sectionKey: string;
  type: 'variables' | 'attributes';
  filterParams?: MyLeadsSearchQuery;
  title: string;
  searchDisabled?: boolean;
  defaultOperatorType?: 'wildcard' | 'prefix';
  className?: string;
  onFetch: (query: string, type?: MyLeadsUrlsQueryType) => Promise<string[]>;
  onUpdate?: (item: string, type: MyLeadsFilterTypes) => void;
  css?: { title?: string };
  exlude?: boolean;
  logicOperator?: 'and';
};

export const LandingPagesRemoteSearch = ({
  sectionKey,
  type,
  filterParams,
  title,
  searchDisabled,
  className,
  css,
  exlude,
  logicOperator,
  defaultOperatorType,
  onFetch,
  onUpdate,
}: Props) => {
  const [urlSearchValue, setUrlSearchValue] = useState('');
  const [fetchUrlResults, setFetchUrlResults] = useState<string[]>([]);

  const setFilterType = (type: 'variables' | 'attributes', match: string) => {
    const typesByInclusion = exlude ? myLeadsExcludeTypes : myLeadsIncludeTypes;
    const key = typesByInclusion.find(t => {
      const lowercasedT = t.toLocaleLowerCase();
      return lowercasedT.includes(type) && lowercasedT.includes(match);
    });
    if (key) {
      return MyLeadsFilterTypes[key].toString();
    }
    return uniqueId(type);
  };

  const exactTypes = setFilterType(type, '');
  const prefixTypes = setFilterType(type, 'prefix');
  const wildcardTypes = setFilterType(type, 'wildcard');

  const operators = useMemo(
    () => [
      {
        id: wildcardTypes,
        name: 'Contains',
      },
      {
        id: exactTypes,
        name: 'Exact',
      },
      {
        id: prefixTypes,
        name: 'Starts with',
      },
    ],
    [],
  );

  const remoteLandings = useMemo(() => {
    const processUrls = (urls: string[], operator: string) =>
      urls?.map(url => ({ url, operator }));

    const mappedVariables = Object.entries(filterParams?.query || {}).flatMap(
      ([key, value]) => {
        const urls =
          Array.isArray(value?.[sectionKey]) && logicOperator
            ? value?.[sectionKey].find(v => v?.[logicOperator])?.[logicOperator]
            : value?.[sectionKey];
        return processUrls(urls, MyLeadsFilterTypes[key]);
      },
    );

    return mappedVariables;
  }, [filterParams?.query, sectionKey, logicOperator]);

  const [selectedUrls, setSelectedUrls] = useState<
    { url: string; operator: string }[]
  >(remoteLandings || []);

  const defaultOperator = operators.find(o =>
    o.id.includes(defaultOperatorType || ''),
  );

  const [selectedOperator, setSelectedOperator] = useState<DropdownOption>(
    (defaultOperator || operators[0]) as DropdownOption,
  );

  const shouldShowAdd = selectedOperator.id === wildcardTypes;

  const getVariableType = (operatorId: string) =>
    MyLeadsFilterTypes[operatorId];

  const clear = () => {
    setUrlSearchValue('');
    setFetchUrlResults([]);
  };

  const onSelectItem = (selectedItem: string, manualInput?: boolean) => {
    const trimmedUrl = selectedItem.trim();
    const isExisting = selectedUrls.find(
      item =>
        item?.url === trimmedUrl && item?.operator === selectedOperator.id,
    );
    if (isExisting) return;

    const operator =
      selectedOperator.id === wildcardTypes && !manualInput
        ? operators.find(o => o.id === prefixTypes)?.id ?? ''
        : selectedOperator.id.toString();

    setSelectedUrls(state => [...state, { url: trimmedUrl, operator }]);
    onUpdate?.(trimmedUrl, getVariableType(operator));
    clear();
  };

  const onDeleteItem = (url: string, operator: string) => {
    setSelectedUrls(state =>
      state.filter(item => item?.url !== url && item?.operator !== operator),
    );
    onUpdate?.(url, getVariableType(operator));
  };

  const renderSuggestion = (suggestion: string) => {
    if (!suggestion) return null;
    return (
      <div className={styles.suggestionContainer} key={suggestion}>
        <div className={styles.suggestion}>{suggestion}</div>
      </div>
    );
  };

  const fetchLandings = async (query: string) => {
    const trimmedQuery = query.trim();
    setUrlSearchValue(trimmedQuery);
    const results = await onFetch(
      trimmedQuery,
      selectedOperator.id === exactTypes ? undefined : 'prefix',
    );
    setFetchUrlResults(results || []);
  };

  useLayoutEffect(() => {
    if (!isEqual(remoteLandings, selectedUrls)) {
      setSelectedUrls(remoteLandings || []);
    }
  }, [remoteLandings]);

  const filelteredSelectedUrlsByType = selectedUrls.filter(item =>
    !exlude
      ? myLeadsIncludeTypes.includes(item?.operator as MyLeadsFilterTypes)
      : myLeadsExcludeTypes.includes(item?.operator as MyLeadsFilterTypes),
  );

  return (
    <div className={classNames(styles.block, className)}>
      <div className={styles.selectors}>
        <div className={styles.operator}>
          <div>Operator</div>
          <PrimarySelect
            className={styles.operatorsSelect}
            options={operators}
            value={selectedOperator}
            onChange={setSelectedOperator}
          />
        </div>
        <div className={styles.searchContainer}>
          <div className={classNames(styles.title, css?.title)}>{title}</div>

          <div className={styles.inputWrapper}>
            <RemoteSearchInput
              value={urlSearchValue}
              clearInputOnSelect
              placeholder={
                selectedOperator.id === exactTypes
                  ? 'Exact URL (min 3 characters)'
                  : 'URL contains (min 3 characters)'
              }
              disabled={searchDisabled}
              renderSuggestion={renderSuggestion}
              onFetch={fetchLandings}
              results={fetchUrlResults}
              onSelectedItem={onSelectItem}
              className={styles.input}
              style={{
                border: '1px solid var(--color-gray-5)',
                borderRadius: '4px',
              }}
              withSearchIcon
              withAddButton={shouldShowAdd}
              disabledAddButton={urlSearchValue.length < 4}
            />
          </div>
        </div>
      </div>
      <div className={styles.selectedValues}>
        {filelteredSelectedUrlsByType?.map(item => (
          <div className={styles.itemWrapper} key={uniqueId(item.url)}>
            <div className={styles.item}>
              <div
                className={classNames(styles.tag, {
                  [styles.greenTag]: item.operator === exactTypes,
                  [styles.purpleTag]: item.operator === wildcardTypes,
                })}
              >
                {operators.find(o => o.id === item.operator)?.name}
              </div>
              <Tooltip
                title={item?.url?.length > 30 ? item.url?.toLowerCase() : ''}
              >
                <div className={styles.selectedUrl} key={item.url}>
                  {item.url?.toLowerCase()}
                </div>
              </Tooltip>
            </div>
            <CloseOutlined
              className={styles.close}
              onClick={() => onDeleteItem(item.url, item.operator)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
